import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const WHATSAPP_NUMBER = '12144620683'
const MAX_QTY = 10

const CartContext = createContext(null)

export const useCart = () => {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be used within CartProvider')
  return ctx
}

export const CartProvider = ({ children }) => {
  const { t, i18n } = useTranslation()
  const [cart, setCart] = useState({})
  const [isCartOpen, setIsCartOpen] = useState(false)

  const items = useMemo(() => Object.values(cart), [cart])
  const totalUnits = useMemo(() => items.reduce((sum, e) => sum + e.qty, 0), [items])
  const subtotal = useMemo(() => items.reduce((sum, e) => sum + (e.price || 0) * e.qty, 0), [items])

  const formatPrice = useCallback((value) => {
    return new Intl.NumberFormat(i18n.language.startsWith('es') ? 'es-US' : 'en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value ?? 0)
  }, [i18n.language])

  const addItem = useCallback((product) => {
    setCart((prev) => {
      const next = { ...prev }
      const existing = next[product.id]
      if (existing) {
        next[product.id] = { ...existing, qty: Math.min(MAX_QTY, existing.qty + 1) }
      } else {
        next[product.id] = {
          id: product.id,
          name: product.name,
          planNumber: product.planNumber,
          price: product.price || 0,
          qty: 1,
        }
      }
      return next
    })
    setIsCartOpen(true)
  }, [])

  const increment = useCallback((id) => {
    setCart((prev) => {
      if (!prev[id]) return prev
      const next = { ...prev }
      next[id] = { ...next[id], qty: Math.min(MAX_QTY, next[id].qty + 1) }
      return next
    })
  }, [])

  const decrement = useCallback((id) => {
    setCart((prev) => {
      if (!prev[id]) return prev
      const next = { ...prev }
      const entry = { ...next[id] }
      entry.qty -= 1
      if (entry.qty <= 0) {
        delete next[id]
      } else {
        next[id] = entry
      }
      return next
    })
  }, [])

  const removeItem = useCallback((id) => {
    setCart((prev) => {
      if (!prev[id]) return prev
      const next = { ...prev }
      delete next[id]
      return next
    })
  }, [])

  const clearCart = useCallback(() => setCart({}), [])

  const whatsappUrl = useMemo(() => {
    if (!items.length) return `https://wa.me/${WHATSAPP_NUMBER}`
    const isEs = i18n.language.startsWith('es')

    const header = isEs ? '🏠 *Pedido de Planos — JD Rivero*' : '🏠 *Plan Order — JD Rivero*'
    const divider = '─'.repeat(28)
    const lines = items.map((e) => {
      const lineTotal = formatPrice(e.price * e.qty)
      return `• ${e.qty} × *${e.name}* (Plan ${e.planNumber})\n  ${lineTotal}`
    })
    const totalLabel = isEs ? '💰 *Total Estimado*' : '💰 *Estimated Total*'
    const totalLine = `${totalLabel}: ${formatPrice(subtotal)}`
    const footer = isEs
      ? '_Enviado desde jdrivero.com/shop_'
      : '_Sent from jdrivero.com/shop_'

    const message = [header, divider, ...lines, divider, totalLine, '', footer].join('\n')
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`
  }, [items, subtotal, formatPrice, i18n.language])

  const value = useMemo(() => ({
    cart,
    items,
    totalUnits,
    subtotal,
    formatPrice,
    addItem,
    increment,
    decrement,
    removeItem,
    clearCart,
    isCartOpen,
    setIsCartOpen,
    whatsappUrl,
  }), [cart, items, totalUnits, subtotal, formatPrice, addItem, increment, decrement, removeItem, clearCart, isCartOpen, whatsappUrl])

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}
